
import React from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { BadgeInfo } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const EnvSetupGuide: React.FC = () => {
  const [isSupabaseConnected, setIsSupabaseConnected] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    const checkSupabaseConnection = async () => {
      try {
        // Try to make a simple query to see if Supabase is connected
        const { data, error } = await supabase
          .from('newsletter_submissions')
          .select('id')
          .single();
          
        // Check if we got a proper response or specific error
        // Error code 'PGRST116' means no rows returned, which is fine
        const isConnected = !error || 
          (error && typeof error === 'object' && 'code' in error && 
           (error.code === 'PGRST116' || error.code === '406'));
           
        setIsSupabaseConnected(isConnected);
      } catch (err) {
        console.error('Error checking Supabase connection:', err);
        setIsSupabaseConnected(false);
      }
    };

    checkSupabaseConnection();
  }, []);

  if (isSupabaseConnected === true) {
    return null; // Supabase is connected, don't show guide
  }

  return (
    <Alert className="max-w-4xl mx-auto mb-8">
      <BadgeInfo className="h-5 w-5" />
      <AlertTitle>Supabase Setup Required</AlertTitle>
      <AlertDescription className="space-y-4">
        <p>
          This calculator is configured to save submissions to a Supabase database and send email notifications.
          To enable these features, please connect your Supabase project.
        </p>
        <div className="space-y-2">
          <p className="font-semibold">Required setup:</p>
          <ol className="list-decimal list-inside space-y-1 pl-4">
            <li>Connect your Lovable project to Supabase using the Supabase menu in the top right</li>
            <li>Create a <code>newsletter_submissions</code> table with the following columns:
              <ul className="list-disc list-inside pl-4 mt-1">
                <li>id (uuid, primary key)</li>
                <li>created_at (timestamp with timezone)</li>
                <li>name (text)</li>
                <li>email (text)</li>
                <li>company (text)</li>
                <li>position (text)</li>
                <li>list_size (integer)</li>
                <li>industry (text)</li>
                <li>frequency (integer)</li>
                <li>calculation_result (jsonb)</li>
              </ul>
            </li>
            <li>Add the following secrets to your Supabase project:
              <ul className="list-disc list-inside pl-4 mt-1">
                <li><code>RESEND_API_KEY</code> - API key for Resend.com email service</li>
                <li><code>ADMIN_EMAIL</code> - The email address where admin notifications should be sent</li>
              </ul>
            </li>
          </ol>
        </div>
        <p>
          <Button variant="outline" onClick={() => window.open('https://docs.lovable.dev/integrations/supabase/', '_blank')}>
            View Supabase Integration Docs
          </Button>
        </p>
      </AlertDescription>
    </Alert>
  );
};

export default EnvSetupGuide;
